import React, { useMemo } from 'react'
import { css } from '@emotion/core'
import { BaybayinText } from '@fs/zion-homelands-shared'
import { colors, H1, Separator } from '@fs/zion-ui'

import fsLogo from '../../../../../images/logos/fs-logo-dark.svg'
import baybayinBgImage from '../images/social-share-bg-baybayin.webp'
import surnameBgImage from '../images/social-share-bg-surname.webp'

const getSocialCss = ({ surnameFontSize, isBaybayinVariant }) => css`
  width: 786px;
  height: 597px;
  background-color: ${colors.blue03};

  display: flex;
  flex-direction: column;

  h1 {
    font-size: ${surnameFontSize}px;
    line-height: 1.05;
    letter-spacing: 0.25px;
    font-weight: 600;
    color: ${colors.gray100};
    margin: 0;
    padding: 0;
    text-align: center;
  }

  h4 {
    opacity: 0.74;
    font-weight: 400 !important;
    color: ${colors.gray100};
  }

  .tagline-container {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .tagline {
    font-size: 35px;
    line-height: 114%;
    letter-spacing: -0.5px;
    font-weight: 600;
    color: ${colors.gray100};
    margin: 0 auto;
    padding: 0;
    text-align: center;
    max-width: 650px;
  }

  .top-fold {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 42px;
    min-height: 280px;
    padding: 42px;
  }

  .bottom-fold {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 42px;
  }

  .image-background-fold {
    background-image: url(${isBaybayinVariant ? baybayinBgImage : surnameBgImage});
    background-size: auto;
    background-position: ${isBaybayinVariant ? 'top' : 'right'};
  }

  .blue-background-fold {
    background-color: ${colors.blue05};
  }
`

export default function SocialShareImage({ surname, recordCount = 0, variant = 'surname' }) {
  // Calculate dynamic font size based on surname name length
  // Max width available is approximately 702px (786px - 42px padding on each side)
  const surnameFontSize = useMemo(() => {
    const baseSize = 120
    const maxWidth = 702
    const charWidth = baseSize * 0.6 // Approximate width per character
    const estimatedWidth = surname.length * charWidth

    if (estimatedWidth <= maxWidth) {
      return baseSize
    }

    // Scale down proportionally
    return Math.max(60, Math.floor((maxWidth / estimatedWidth) * baseSize))
  }, [surname])

  const isBaybayinVariant = variant === 'baybayin'

  return (
    <div css={getSocialCss({ surnameFontSize, isBaybayinVariant })}>
      {isBaybayinVariant ? (
        <>
          <div className="top-fold image-background-fold">
            <BaybayinText text={surname} maxFontSize={120} as="h1" />
          </div>
          <div className="bottom-fold blue-background-fold">
            <div className="tagline-container">
              <p className="tagline">I discovered my surname in Baybayin on FamilySearch!</p>
            </div>
            <img src={fsLogo} alt="FamilySearch" style={{ width: 124, margin: '0 auto' }} />
          </div>
        </>
      ) : (
        <>
          <div className="top-fold blue-background-fold">
            <BaybayinText text={surname} maxFontSize={70} as="h4" />
            <Separator size="md" />
            <H1 supplementary>{surname}</H1>
          </div>
          <div className="bottom-fold image-background-fold">
            <p className="tagline">
              My Surname is Found {recordCount?.toLocaleString()} Times in FamilySearch Records!
            </p>
            <img src={fsLogo} alt="FamilySearch" style={{ width: 124, margin: '0 auto' }} />
          </div>
        </>
      )}
    </div>
  )
}
